/**
 * DRAWING UTILS (MEV4)
 * Logic for SVG path generation, peak detection, and label staggering.
 */

import { SimulationResultPoint } from './engine-v4';

export interface SvgPeak {
    x: number;
    y: number;
    val: string;
    color: string;
    type: string;
}

export const DrawingUtils = {
    
    /**
     * Detects all local maxima (bumps) in the simulation data.
     */
    calculateLocalMaxima(
        simulation: SimulationResultPoint[], 
        types: { id: string, color: string }[],
        visibleLines: Record<string, boolean>,
        scaleY: (val: number) => number
    ): SvgPeak[] {
        if (!simulation || simulation.length < 3) return [];
        const peaks: SvgPeak[] = [];

        types.forEach(type => {
            if (!visibleLines[type.id]) return;
            
            for (let i = 1; i < simulation.length - 1; i++) {
                const getVal = (idx: number) => {
                    const p = simulation[idx].state.fractions;
                    if (type.id === 'protein') return p.intact_protein.blood + p.free_amino_acids.blood;
                    if (type.id === 'glucose') return p.glucose.blood;
                    if (type.id === 'fructose') return p.fructose.blood;
                    if (type.id === 'starch') return p.complex_carbs.blood;
                    if (type.id === 'fat') return p.lipids.blood;
                    if (type.id === 'insulin') return simulation[idx].state.insulin;
                    return 0;
                };

                const prev = getVal(i - 1);
                const curr = getVal(i);
                const next = getVal(i + 1);

                // Significance Threshold: Ignore tiny noise bumps
                const threshold = type.id === 'insulin' ? 5 : 0.5;

                if (curr > prev && curr > next && curr > threshold) {
                    peaks.push({ 
                        x: simulation[i].x, 
                        y: scaleY(curr), 
                        val: curr.toFixed(1), 
                        color: type.color,
                        type: type.id
                    });
                }
            }
        });
        return peaks;
    },

    /**
     * Staggers overlapping labels vertically to prevent text collision.
     */
    staggerLabels(peaks: SvgPeak[]): SvgPeak[] {
        // Sort peaks by X position
        const sorted = [...peaks].sort((a, b) => a.x - b.x);
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1];
            const curr = sorted[i];
            
            // If peaks are horizontally within 5% of each other
            if (Math.abs(curr.x - prev.x) < 5) {
                // If they are vertically close, drop the current one
                if (Math.abs(curr.y - prev.y) < 15) {
                    curr.y += 15;
                }
            }
        }
        return sorted;
    }
};
